import BalanceRow from '@components/BalanceRow'
import PerpBalanceRow from '@components/PerpBalanceRow'
import { Titlebar } from '@components/Titlebar/Titlebar'
import { PERP_FUNDING_SOURCE_IDS } from '@plug-core/constants/business'
import { USDC_ARB_ID, USDC_HYPERCORE_PERP_ID } from '@plug-core/constants/tokenIds'
import usePerpActivity from '@plug-core/hooks/perps/usePerpActivity'
import usePerpAvailableBalance from '@plug-core/hooks/perps/usePerpAvailableBalance'
import useBalances from '@plug-core/hooks/useBalances'
import useConfig from '@plug-core/hooks/useConfig'
import { PerpetualToken } from '@plug-core/providers/ConfigProvider'
import { usePerpsSettings } from '@plug-core/state'
import { formatMoney } from '@plug-core/utils/formatNumber'
import { Button, Heading, Text } from '@ui'
import { ROUTES } from '@utils/routes'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import PerpActivityRow from './Components/PerpActivityRow'
import PerpsTutorial from './Components/PerpsTutorial'

const PerpsList = () => {
  const { tokens, perpsTokens, tokenPricesWithChange } = useConfig()
  const navigate = useNavigate()
  const { perpsAccountBalance } = useBalances()
  const availableBalance = usePerpAvailableBalance()
  const { t } = useTranslation()
  const { hasSeenIntro, setHasSeenIntro } = usePerpsSettings()
  const { activities } = usePerpActivity()

  const [showTutorial, setShowTutorial] = useState(false)

  // Wait for the view to render before showing intro modal to avoid visual bugs
  useEffect(() => {
    setTimeout(() => setShowTutorial(!hasSeenIntro), 200)
  }, [hasSeenIntro])

  const renderPerpsBalance = () => (
    <div className="flex flex-col items-center">
      <Heading level="display" as="div" tabular>
        {formatMoney(perpsAccountBalance?.totalBalance || 0)}
      </Heading>
      <Text variant="body-l" tone="fg-2" tabular className="-mt-1">
        Available: {formatMoney(availableBalance)}
      </Text>

      <div className="mt-4 flex w-full gap-4">
        <Button
          variant="primary"
          fullWidth
          className="flex-1"
          onClick={() =>
            navigate(ROUTES.SWAP, {
              state: {
                toTokenId: USDC_HYPERCORE_PERP_ID,
                lockToToken: true,
                sourceTokenIds: PERP_FUNDING_SOURCE_IDS,
              },
            })
          }
        >
          {t('perps.addFunds')}
        </Button>

        <Button
          variant="secondary"
          fullWidth
          className="flex-1"
          onClick={() =>
            navigate(ROUTES.SWAP, {
              state: {
                fromTokenId: USDC_HYPERCORE_PERP_ID,
                toTokenId: USDC_ARB_ID,
                lockFromToken: true,
              },
            })
          }
        >
          {t('perps.withdraw')}
        </Button>
      </div>
    </div>
  )

  const renderPerpItem = (perp: PerpetualToken) => {
    const token = tokens.find((t) => t.additionalData.hyperliquidId === perp.id.toString())
    const tokenPriceWithChange = tokenPricesWithChange[token?.id || '']

    return (
      <BalanceRow
        key={perp.id}
        logo={token?.logo}
        title={perp.name}
        subtitle={`Max Leverage: ${perp.maxLeverage}x`}
        value={formatMoney(tokenPriceWithChange?.price || 0)}
        valueChange={tokenPriceWithChange?.priceChange}
        onPress={() => navigate(ROUTES.PERP_DETAILS(perp.id.toString()))}
      />
    )
  }

  const positions = perpsAccountBalance?.assetPositions || []

  return (
    <div className="mb-3 flex flex-col">
      <Titlebar onActionClick={() => navigate(-1)} />

      {renderPerpsBalance()}

      {positions.length > 0 && (
        <>
          <Heading level={4} className="mt-4">
            {t('perps.positions')}
          </Heading>
          {positions.map((position) => (
            <PerpBalanceRow key={position.position.coin} position={position} />
          ))}
        </>
      )}

      <Heading level={4} className="mt-4">
        {t('perps.perps')}
      </Heading>

      {perpsTokens.map(renderPerpItem)}

      <Heading level={4} className="mt-4">
        {t('perps.activity')}
      </Heading>

      {activities.slice(0, 5).map((activity) => (
        <PerpActivityRow key={activity.id} perpActivity={activity} />
      ))}

      <BalanceRow
        logo="https://plug-cdn.s3.amazonaws.com/chain-logos/hyperliquid.png"
        title={t('perps.perps')}
        subtitle={t('perps.learnBasics')}
        onPress={() => setHasSeenIntro(false)}
      />

      <PerpsTutorial visible={showTutorial} />
    </div>
  )
}

export default PerpsList
